import { forwardRef, useState } from 'react';

import { cn, ia, mString } from 'lib/helpers/utility';
import { resolveCardIcon } from 'lib/helpers/resolveCardIcon';

import Dot from 'components/shared/Dot/Dot';
import Icon from 'components/shared/Icon/Icon';
import Button from 'components/shared/Button/Button';
import Disclosure from 'components/shared/Disclosure/Disclosure';
import Typography from 'components/shared/Typography/Typography';
import {
  usePaymentContext,
  usePaymentContextApi
} from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';

export const Caption = ({ card }) => {
  if (!card.expiration || !card.pan) return null;

  const expiration = `${card.expiration?.slice(0, 2)}/${card.expiration?.slice(2, 4)}`;
  const last4 = card.pan?.slice(-4);
  return (
    <div className="flex items-center gap-1.5">
      <Typography variant="p" color="neutral-600" weight="medium" size="xs">
        **** {last4}
      </Typography>
      <Dot size="xs" color="neutral" />
      <Typography variant="p" color="neutral-600" weight="medium" size="xs">
        Expires {expiration}
      </Typography>
    </div>
  );
};

const SavedCards = forwardRef(({ cards, isFetchingNextPage, hideButton, className }, ref) => {
  const [error, setError] = useState(null);
  const { values, isCharging } = usePaymentContext();
  const { handlePayment, setFieldValue } = usePaymentContextApi();

  if (!ia(cards)) return null;

  const handlePaySavedCard = async () => {
    if (!values.methodDetail) {
      setError('Select a card to pay with');
      return;
    }
    if (error) setError(null);
    await handlePayment();
  };

  const onToggle = (id) => {
    if (error) setError(null);
    setFieldValue('methodDetail', id);
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <div ref={ref} className={cn('max-h-80 overflow-y-auto', className)}>
        <div className="flex flex-col gap-2 p-2">
          {error && (
            <Typography variant="p" color="danger-500" size="xs">
              {error}
            </Typography>
          )}
          {cards.map((card) => {
            const isActive = values.methodDetail === card.id;
            return (
              <Disclosure
                key={card.id}
                isOpen={isActive}
                icon={<Icon icon={resolveCardIcon(card.pan)} size="" />}
                onToggle={(open) => onToggle(open ? card.id : null)}
                disableOpen
                caption={<Caption card={card} />}
                bodyClassName="flex-col"
                buttonClassName="pt-2.5 pb-1.5"
                className="flex-shrink-0"
                customTitle={
                  <Typography variant="label" size="medium">
                    {card.name}
                  </Typography>
                }
              />
            );
          })}
        </div>
        {isFetchingNextPage && (
          <div className="align-center flex justify-center pb-2">
            <Icon icon="loader-linear" className="animate-spin" color="primary" size={24} />
          </div>
        )}
      </div>
      {!hideButton && (
        <div className="flex justify-end">
          <Button
            className="justify-end"
            text={`Pay ${mString(values.amount)}`}
            onClick={handlePaySavedCard}
            size="small"
            isLoading={isCharging}
          />
        </div>
      )}
    </div>
  );
});

export default SavedCards;
